import { useEffect, useRef, useState } from 'react'

import type { AdminStaffTarget, AdminUserRow } from '../../api/admin'

/**
 * Buscador de la cuenta sobre la que se va a operar en el backoffice — darle
 * un rol de staff, o sumarla al plantel del club (mg, 2026-09-10: *"pide como
 * entries user_id, no funciona pq no sabemos los user ids... debería poder
 * ponerse mail o buscar entre los existentes"*; mismo pedido para jugadores el
 * 2026-09-17).
 *
 * Dos caminos, los dos válidos: elegir de la lista de resultados (queda el
 * `user_id`, sin ambigüedad) o pegar el mail completo y mandar (el backend
 * resuelve email o username, ver `add_club_staff` y `assign_players`). Lo que
 * NO se puede es mandar media palabra suelta: eso es una búsqueda sin elegir,
 * no un destinatario.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Cuántos resultados se muestran. Más que esto no se lee: si no está entre
 *  los primeros, la respuesta es afinar la búsqueda, no scrollear. */
export const MAX_RESULTS = 8

/** Mínimo de caracteres antes de pegarle al backend por cada tecla. */
export const MIN_QUERY = 2

export const SEARCH_DEBOUNCE_MS = 250

export type UserTargetResolution =
  | { target: AdminStaffTarget; label: string }
  | { error: string }

/** Qué se manda al backend con lo que hay en el form, o por qué no alcanza. */
export function resolveUserTarget(
  query: string,
  picked: AdminUserRow | null,
): UserTargetResolution {
  if (picked) {
    return { target: { user_id: picked.id }, label: userLabel(picked) }
  }
  const raw = query.trim().toLowerCase()
  if (!raw) {
    return { error: 'Buscá a la persona por email (o nombre) y elegila de la lista.' }
  }
  if (!EMAIL_RE.test(raw)) {
    return {
      error:
        'Elegí un usuario de la lista, o escribí el email completo de una ' +
        'cuenta que ya exista.',
    }
  }
  return { target: { email: raw }, label: raw }
}

/** Cómo se nombra a una cuenta en la lista y en la confirmación: el email
 *  primero, que es lo que el operador reconoce. */
export function userLabel(u: AdminUserRow): string {
  const identity = u.email ?? u.username
  return u.display_name ? `${u.display_name} — ${identity}` : identity
}

export function UserPicker({
  query,
  onQueryChange,
  picked,
  onPick,
  search,
  disabled = false,
  notFoundHint,
  actionLabel = 'Vas a agregar',
}: {
  query: string
  onQueryChange: (q: string) => void
  picked: AdminUserRow | null
  onPick: (u: AdminUserRow | null) => void
  search: (q: string) => Promise<AdminUserRow[]>
  disabled?: boolean
  /** Qué ofrecer cuando la búsqueda no encuentra a nadie: el camino de salida
   *  no es el mismo para staff (invitación) que para un jugador (crear la
   *  cuenta), así que lo pone el caller. */
  notFoundHint?: React.ReactNode
  actionLabel?: string
}) {
  const [results, setResults] = useState<AdminUserRow[] | null>(null)
  const [searching, setSearching] = useState(false)
  const runId = useRef(0)
  // Por ref y no como dependencia del efecto: el caller la define inline, así
  // que cambia de identidad en cada render y dispararía la búsqueda en loop.
  const searchRef = useRef(search)
  searchRef.current = search

  useEffect(() => {
    const raw = query.trim()
    if (picked || raw.length < MIN_QUERY) {
      setResults(null)
      setSearching(false)
      return
    }
    const run = ++runId.current
    setSearching(true)
    const t = setTimeout(() => {
      searchRef.current(raw)
        .then((users) => {
          // Descartar respuestas de una búsqueda vieja: con debounce corto
          // llegan fuera de orden y pisarían lo que el operador está viendo.
          if (run !== runId.current) return
          setResults(users.slice(0, MAX_RESULTS))
        })
        .catch(() => {
          if (run === runId.current) setResults([])
        })
        .finally(() => {
          if (run === runId.current) setSearching(false)
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query, picked])

  if (picked) {
    return (
      <div className="flex items-center gap-sm">
        <span className="text-sm text-text-primary">
          {actionLabel}: <strong>{userLabel(picked)}</strong>
        </span>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => {
            onPick(null)
            onQueryChange('')
          }}
        >
          Cambiar
        </button>
      </div>
    )
  }

  return (
    <div className="flex w-full max-w-[420px] flex-col gap-xs">
      <input
        type="text"
        placeholder="email o nombre del usuario"
        value={query}
        disabled={disabled}
        onChange={(e) => onQueryChange(e.target.value)}
        className="input"
        aria-label="Buscar usuario por email o nombre"
        autoComplete="off"
      />
      {searching && <span className="text-xs text-text-muted">Buscando…</span>}
      {!searching && results?.length === 0 && (
        <span className="text-xs text-text-muted">
          Ningún usuario coincide con “{query.trim()}”.{' '}
          {notFoundHint ?? 'Si todavía no tiene cuenta, hay que crearla primero.'}
        </span>
      )}
      {!searching && results != null && results.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-xs p-0">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className="choice-card py-sm text-sm"
                onClick={() => {
                  onPick(u)
                  onQueryChange(u.email ?? u.username)
                }}
              >
                <span className="flex-1">{userLabel(u)}</span>
                <span className="tag">#{u.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
